import React, { useState } from 'react';
import { Box, TextField, InputAdornment, Avatar, Typography, IconButton, Popover, useTheme, useMediaQuery, Drawer, List, ListItem, ListItemText } from '@mui/material';
import {
  Search as SearchIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// Custom Notification Icon Component
const CustomNotificationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="26" viewBox="0 0 24 26" fill="none">
    <path
      d="M22.5 13.5C23.3284 13.5 24 14.1716 24 15V16.5C24 17.3284 23.3284 18 22.5 18H1.5C0.671573 18 2.41598e-08 17.3284 0 16.5V15C0 14.1716 0.671573 13.5 1.5 13.5H4.5L5.55566 4.00293C5.80895 1.72419 7.73459 0.000174713 10.0273 0H13.9727C16.2654 0.000174713 18.1911 1.72419 18.4443 4.00293L19.5 13.5H22.5Z"
      fill="#36C5F0"
    />
    <rect opacity="0.9" x="9" y="19.5" width="6" height="6" rx="2.25" fill="white" />
  </svg>
);

const Header = ({ sidebarWidth = 72 }) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [userInfo, setUserInfo] = useState({ firstname: 'User', lastname: '', role: 'Admin' });
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Media queries for better responsive design
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 960px
  const isSmallDesktop = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 960px - 1280px
  // const isMediumDesktop = useMediaQuery(theme.breakpoints.between('lg', 'xl')); // 1280px - 1920px
  const is1440Screen = screenSize.width === 1440; // Specific handling for 1440px screens
  
  // Responsive breakpoints based on current screen size
  // const isSmallLaptop = screenSize.width <= 1366; // Small laptop (1366px and below)
  // const isMediumLaptop = screenSize.width > 1366 && screenSize.width <= 1600; // Medium laptop (1367px - 1600px)
  // const isLargeLaptop = screenSize.width > 1600; // Large laptop (1601px and above)

  // Mobile menu handlers
  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleSearchToggle = () => {
    setSearchExpanded(!searchExpanded);
  };

  // Get user info from localStorage
  React.useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserInfo({
          firstname: user.firstname || 'User',
          lastname: user.lastname || '',
          role: user.role || 'Admin'
        });
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Handle scroll event for header background
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle window resize for responsive design
  React.useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const initials = `${userInfo.firstname?.[0]?.toUpperCase() || ''}${userInfo.lastname?.[0]?.toUpperCase() || ''}` || 'U';

  // const handleCalendarClick = (event) => {
  //   setCalendarAnchor(event.currentTarget);
  // };

  const handleCalendarClose = () => {
    setCalendarAnchor(null);
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    // You can add logic here to handle date changes
    console.log('Date changed to:', newDate);
  };

  const styles = {
    container: {
      position: 'fixed',
      top: 0,
      left: isMobile || isTablet ? 0 : sidebarWidth,
      right: 0,
      height: isMobile ? 56 : isTablet ? 60 : isSmallDesktop ? 64 : 68,
      background: isScrolled 
        ? 'linear-gradient(110deg, rgba(6,11,38,0.95) 74.67%, rgba(6,11,40,0.35) 100%)' 
        : 'linear-gradient(110deg, rgba(6,11,38,0.02) 74.67%, rgba(6,11,40,0.01) 100%)',
      borderBottom: isScrolled ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.02)',
      backdropFilter: 'blur(20px)',
      zIndex: 130,
      display: 'grid',
      gridTemplateColumns: 'auto 1fr auto',
      alignItems: 'center',
      padding: isMobile ? '0 12px' : isTablet ? '0 16px' : isSmallDesktop ? '0 20px' : '0 28px',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: isScrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
    },
    leftSection: {
      display: 'flex',
      alignItems: 'center',
      minWidth: isMobile ? 40 : isTablet ? 60 : isSmallDesktop ? 120 : 160,
      gap: 1
    },
    hamburgerButton: {
      display: isMobile || isTablet ? 'flex' : 'none',
      color: 'rgba(255, 255, 255, 0.8)',
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      width: isMobile ? 36 : 40,
      height: isMobile ? 36 : 40,
      borderRadius: '8px',
      backdropFilter: 'blur(10px)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.12)'
      },
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    mobileSearchButton: {
      display: isMobile ? 'flex' : 'none',
      color: 'rgba(255, 255, 255, 0.8)',
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      width: 36,
      height: 36,
      borderRadius: '8px',
      backdropFilter: 'blur(10px)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)'
      }
    },
    breadcrumb: {
      color: 'rgba(255, 255, 255, 0.8)',
      fontSize: 14,
      fontWeight: 400,
      fontFamily: 'Plus Jakarta Sans, sans-serif'
    },
    searchContainer: {
      display: isMobile ? (searchExpanded ? 'flex' : 'none') : 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      position: isMobile && searchExpanded ? 'absolute' : 'relative',
      top: isMobile && searchExpanded ? 0 : 'auto',
      left: isMobile && searchExpanded ? 0 : 'auto',
      right: isMobile && searchExpanded ? 0 : 'auto',
      zIndex: isMobile && searchExpanded ? 140 : 'auto',
      backgroundColor: isMobile && searchExpanded ? 'rgba(6,11,38,0.98)' : 'transparent',
      padding: isMobile && searchExpanded ? '12px' : 0,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    searchField: {
      '& .MuiOutlinedInput-root': {
        width: isMobile ? '100%' : isTablet ? '240px' : isSmallDesktop ? '280px' : is1440Screen ? '320px' : '360px',
        height: isMobile ? '40px' : isTablet ? '36px' : isSmallDesktop ? '38px' : is1440Screen ? '38px' : '40px',
        flexShrink: 0,
        borderRadius: isMobile ? '20px' : isTablet ? '18px' : is1440Screen ? '19px' : '20px',
        border: '0.6px solid rgba(207, 207, 207, 0.11)',
        background: 'linear-gradient(96deg, #050C28 0.29%, rgba(10, 14, 35, 0.49) 98.84%)',
        color: 'white',
        fontFamily: 'Plus Jakarta Sans, sans-serif',
        fontSize: isMobile ? '14px' : isTablet ? '13px' : is1440Screen ? '13px' : '14px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(10px)',
        '&:hover': {
          border: '0.6px solid rgba(207, 207, 207, 0.2)'
        },
        '&.Mui-focused': {
          border: '1px solid rgba(255, 255, 255, 0.3)',
          background: 'linear-gradient(127deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)',
          backdropFilter: 'blur(60px)'
        },
        '&:active': {
          border: '1px solid rgba(255, 255, 255, 0.3)',
          background: 'linear-gradient(127deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)',
          backdropFilter: 'blur(60px)'
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none'
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          border: 'none'
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: 'none'
        }
      },
      '& .MuiOutlinedInput-root .MuiInputAdornment-positionEnd': {
        display: isMobile && searchExpanded ? 'flex' : 'none'
      },
      '& .MuiInputBase-input': {
        color: 'white',
        fontFamily: 'Plus Jakarta Sans, sans-serif',
        fontSize: isMobile ? '14px' : isTablet ? '13px' : is1440Screen ? '13px' : '14px',
        padding: isMobile ? '8px 12px' : isTablet ? '6px 8px' : is1440Screen ? '6px 8px' : '8px 12px',
        '&::placeholder': {
          color: 'rgba(255, 255, 255, 0.6)',
          opacity: 1,
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontSize: isMobile ? '14px' : isTablet ? '13px' : is1440Screen ? '13px' : '14px'
        }
      },
      '& .MuiInputAdornment-root': {
        color: 'rgba(255, 255, 255, 0.6)',
        '& .MuiSvgIcon-root': {
          fontSize: isMobile ? '20px' : isTablet ? '18px' : '20px'
        }
      }
    },
    searchCloseButton: {
      display: isMobile && searchExpanded ? 'flex' : 'none',
      color: 'rgba(255, 255, 255, 0.8)',
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      width: 36,
      height: 36,
      borderRadius: '8px',
      marginLeft: 1,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)'
      }
    },
    rightSection: {
      display: searchExpanded && isMobile ? 'none' : 'flex',
      alignItems: 'center',
      gap: isMobile ? 1 : isTablet ? 2 : isSmallDesktop ? 2.5 : is1440Screen ? 3 : 3.5,
      justifyContent: 'flex-end',
      flexShrink: 0
    },
    notificationButton: {
      color: 'rgba(255, 255, 255, 0.8)',
      backgroundColor: 'transparent',
      border: 'none',
      width: isMobile ? 32 : isTablet ? 36 : isSmallDesktop ? 38 : is1440Screen ? 38 : 42,
      height: isMobile ? 32 : isTablet ? 36 : isSmallDesktop ? 38 : is1440Screen ? 38 : 42,
      borderRadius: isMobile ? '6px' : isTablet ? '8px' : is1440Screen ? '9px' : '10px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        backdropFilter: 'blur(10px)',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      },
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    calendarButton: {
      color: 'rgba(255, 255, 255, 0.8)',
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      },
      transition: 'all 0.2s ease'
    },
    profileSection: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? 0.75 : isTablet ? 1 : is1440Screen ? 1 : 1.25,
      padding: isMobile ? '4px 6px' : isTablet ? '6px 8px' : is1440Screen ? '6px 8px' : '8px 10px',
      borderRadius: isMobile ? '8px' : isTablet ? '10px' : is1440Screen ? '10px' : '12px',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        backdropFilter: 'blur(10px)',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }
    },
    avatar: {
      width: isMobile ? 26 : isTablet ? 28 : isSmallDesktop ? 30 : is1440Screen ? 30 : 34,
      height: isMobile ? 26 : isTablet ? 28 : isSmallDesktop ? 30 : is1440Screen ? 30 : 34,
      backgroundColor: '#36C5F0',
      fontSize: isMobile ? 10 : isTablet ? 11 : isSmallDesktop ? 12 : is1440Screen ? 12 : 14,
      fontWeight: 600,
      border: '2px solid rgba(255, 255, 255, 0.15)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'scale(1.05)',
        border: '2px solid rgba(255, 255, 255, 0.25)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
      }
    },
    userInfo: {
      display: isMobile || isTablet ? 'none' : 'block',
      opacity: 1,
      transition: 'opacity 0.3s ease'
    },
    userName: {
      color: 'white',
      fontSize: isSmallDesktop ? 12 : is1440Screen ? 12 : 14,
      fontWeight: 500,
      lineHeight: 1.2
    },
    userRole: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: isSmallDesktop ? 10 : is1440Screen ? 10 : 12,
      lineHeight: 1.2
    },
    mobileDrawer: {
      '& .MuiDrawer-paper': {
        width: 280,
        background: 'linear-gradient(135deg, rgba(6,11,38,0.95) 0%, rgba(10,14,35,0.98) 100%)',
        backdropFilter: 'blur(20px)',
        border: 'none',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)'
      }
    },
    calendarPopover: {
      '& .MuiPaper-root': {
        background: 'rgba(6,11,38,0.95)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        color: 'white'
      }
    }
  };

  return (
    <>
      <Box sx={styles.container}>
        {/* Left Section - Mobile Menu & Breadcrumb */}
        <Box sx={styles.leftSection}>
          {/* Hamburger Menu Button for Mobile/Tablet */}
          <IconButton 
            sx={styles.hamburgerButton} 
            onClick={handleMobileMenuToggle}
            size="small"
          >
            <MenuIcon />
          </IconButton>
          
          {/* Mobile Search Button */}
          <IconButton 
            sx={styles.mobileSearchButton} 
            onClick={handleSearchToggle}
            size="small"
          >
            <SearchIcon />
          </IconButton>
        </Box>

        {/* Center - Search Bar and Right Section */}
        <Box sx={styles.rightSection}>
          {/* Search Bar */}
          <Box sx={styles.searchContainer}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={styles.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: isMobile && searchExpanded ? (
                  <InputAdornment position="end">
                    <IconButton 
                      sx={styles.searchCloseButton} 
                      onClick={handleSearchToggle}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Box>

          {/* Notification Bell */}
          <IconButton sx={styles.notificationButton} size="small">
            <CustomNotificationIcon />
          </IconButton>

          {/* Profile Section */}
          <Box sx={styles.profileSection}>
            <Avatar sx={styles.avatar}>{initials}</Avatar>
            <Box sx={styles.userInfo}>
              <Typography sx={styles.userName}>{userInfo.firstname}</Typography>
              <Typography sx={styles.userRole}>{userInfo.role}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={styles.mobileDrawer}
      >
        <Box sx={{ p: 2, pt: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', mb: 2, fontFamily: 'Plus Jakarta Sans' }}>
            Navigation
          </Typography>
          <List>
            <ListItem button sx={{ borderRadius: 1, mb: 1, '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } }}>
              <ListItemText 
                primary="Dashboard" 
                sx={{ '& .MuiTypography-root': { color: 'white', fontFamily: 'Plus Jakarta Sans' } }} 
              />
            </ListItem>
            <ListItem button sx={{ borderRadius: 1, mb: 1, '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } }}>
              <ListItemText 
                primary="Stores" 
                sx={{ '& .MuiTypography-root': { color: 'white', fontFamily: 'Plus Jakarta Sans' } }} 
              />
            </ListItem>
            <ListItem button sx={{ borderRadius: 1, mb: 1, '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } }}>
              <ListItemText 
                primary="Cameras" 
                sx={{ '& .MuiTypography-root': { color: 'white', fontFamily: 'Plus Jakarta Sans' } }} 
              />
            </ListItem>
            <ListItem button sx={{ borderRadius: 1, mb: 1, '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } }}>
              <ListItemText 
                primary="Team" 
                sx={{ '& .MuiTypography-root': { color: 'white', fontFamily: 'Plus Jakarta Sans' } }} 
              />
            </ListItem>
            <ListItem button sx={{ borderRadius: 1, mb: 1, '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } }}>
              <ListItemText 
                primary="Profile" 
                sx={{ '& .MuiTypography-root': { color: 'white', fontFamily: 'Plus Jakarta Sans' } }} 
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Calendar Popover */}
      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={handleCalendarClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: styles.calendarPopover
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={selectedDate}
            onChange={handleDateChange}
            slotProps={{
              textField: {
                sx: {
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    }
                  }
                }
              }
            }}
          />
        </LocalizationProvider>
      </Popover>
    </>
  );
};

export default Header;
